import { useState, useCallback, useMemo } from 'react';
import { useLanguage } from '../../../../contexts/LanguageContext';
import {
    useGetSaleListingListQuery,
    useGetMyPurchaseRequestsQuery
} from '../../../../api/endpoints/vehicleSale';
import { LISTING_STATUS } from '../utils/statusBadges';

export const TAB_KEYS = {
    ALL: 'all',
    MINE: 'mine',
    REQUESTS: 'requests'
};

const initialListingFilters = {
    chassisNo: '',
    brand: '',
    model: '',
    sellerBranchCode: '',
    status: LISTING_STATUS.ACTIVE,
    pageNumber: 1,
    pageSize: 20
};

const initialRequestFilters = {
    chassisNo: '',
    status: null,
    pageNumber: 1,
    pageSize: 20
};

const useVehiclesForSale = () => {
    const { th, getCurrentLanguageId } = useLanguage();
    const languageId = getCurrentLanguageId();
    const [activeTabIndex, setActiveTabIndex] = useState(0);
    const [listingFilters, setListingFilters] = useState(initialListingFilters);
    const [requestFilters, setRequestFilters] = useState(initialRequestFilters);

    const tabConfig = useMemo(
        () => [
            {
                key: TAB_KEYS.ALL,
                label: th('vehiclesForSale.tabs.all'),
                icon: 'pi pi-globe'
            },
            {
                key: TAB_KEYS.MINE,
                label: th('vehiclesForSale.tabs.mine'),
                icon: 'pi pi-user'
            },
            {
                key: TAB_KEYS.REQUESTS,
                label: th('vehiclesForSale.tabs.requests'),
                icon: 'pi pi-inbox'
            }
        ],
        [th]
    );

    const activeTabKey = tabConfig[activeTabIndex]?.key || TAB_KEYS.ALL;

    const onlyMine = activeTabKey === TAB_KEYS.MINE;
    const isListingsTab = activeTabKey === TAB_KEYS.ALL || activeTabKey === TAB_KEYS.MINE;

    const listingsQueryArg = useMemo(
        () => ({ ...listingFilters, onlyMine, languageId }),
        [listingFilters, onlyMine, languageId]
    );

    const requestsQueryArg = useMemo(
        () => ({ ...requestFilters, languageId }),
        [requestFilters, languageId]
    );

    const {
        data: listingsData,
        isFetching: isListingsFetching,
        refetch: refetchListings
    } = useGetSaleListingListQuery(listingsQueryArg, { skip: !isListingsTab });

    const {
        data: requestsData,
        isFetching: isRequestsFetching,
        refetch: refetchRequests
    } = useGetMyPurchaseRequestsQuery(requestsQueryArg, { skip: isListingsTab });

    const handleApplyListingFilters = useCallback((newFilters) => {
        setListingFilters((prev) => ({ ...prev, ...newFilters, pageNumber: 1 }));
    }, []);

    const handleResetListingFilters = useCallback(() => {
        setListingFilters(initialListingFilters);
    }, []);

    const handleApplyRequestFilters = useCallback((newFilters) => {
        setRequestFilters((prev) => ({ ...prev, ...newFilters, pageNumber: 1 }));
    }, []);

    const handleResetRequestFilters = useCallback(() => {
        setRequestFilters(initialRequestFilters);
    }, []);

    const handleListingPageChange = useCallback(({ pageNumber, pageSize }) => {
        setListingFilters((prev) => ({ ...prev, pageNumber, pageSize }));
    }, []);

    const handleRequestPageChange = useCallback(({ pageNumber, pageSize }) => {
        setRequestFilters((prev) => ({ ...prev, pageNumber, pageSize }));
    }, []);

    const refetch = useCallback(() => {
        if (isListingsTab) {
            refetchListings();
        } else {
            refetchRequests();
        }
    }, [isListingsTab, refetchListings, refetchRequests]);

    return {
        th,
        activeTabIndex,
        setActiveTabIndex,
        activeTabKey,
        tabConfig,
        listingFilters,
        requestFilters,
        listings: listingsData?.items || [],
        listingsTotal: listingsData?.totalCount || 0,
        requests: requestsData?.items || [],
        requestsTotal: requestsData?.totalCount || 0,
        isLoading: isListingsTab ? isListingsFetching : isRequestsFetching,
        handleApplyListingFilters,
        handleResetListingFilters,
        handleApplyRequestFilters,
        handleResetRequestFilters,
        handleListingPageChange,
        handleRequestPageChange,
        refetch
    };
};

export default useVehiclesForSale;
